import { useEffect, useRef } from "react";
import { Message, MessageSender } from "../store/types";
import { useMessageStore } from "../store/message";
import { markAsRead } from "../api/message";

export const useMessageRead = (msg: Message) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const hasReportedRef = useRef(false);
  const markMessageAsRead = useMessageStore((state) => state.markMessageAsRead);

  // 全局批量上报队列
  const reportQueue = new Map();
  let reportTimer: NodeJS.Timeout | null = null;
  const MAX_BATCH_SIZE = 20; // 最大批量上报数量
  const MIN_REPORT_DELAY = 100; // 最小上报延迟(ms)
  const MAX_REPORT_DELAY = 2000; // 最大上报延迟(ms)

  // 批量上报函数
  const batchReport = async () => {
    if (reportQueue.size === 0) return;

    try {
      const messagesToReport = Array.from(reportQueue.values()).slice(
        0,
        MAX_BATCH_SIZE,
      );
      const messageIds = messagesToReport.map((msg) => msg.id || msg.clientId);
      try {
        await markAsRead(messageIds);
      } catch (error) {
        console.log(error);
      }

      // 从队列中移除已上报的消息(若上报接口失败，也不再上报了)
      messagesToReport.forEach((msg) => {
        reportQueue.delete(msg.key);
        if (msg.observer) {
          msg.observer.disconnect();
        }
      });
    } catch (error) {
      // console.error("上报失败:", error);
      // 失败重试逻辑
      setTimeout(batchReport, 1000);
    } finally {
      // 重置定时器
      reportTimer = null;
      scheduleNextReport();
    }
  };

  // 安排下一次上报
  const scheduleNextReport = () => {
    if (reportQueue.size === 0) return;

    // 动态计算上报延迟：消息越多，上报越快
    const queueSize = reportQueue.size;
    const delay = Math.max(
      MIN_REPORT_DELAY,
      Math.min(MAX_REPORT_DELAY, MAX_REPORT_DELAY - queueSize * 50),
    );

    if (!reportTimer) {
      reportTimer = setTimeout(batchReport, delay);
    }
  };

  useEffect(() => {
    if (
      !itemRef.current ||
      msg.senderType === MessageSender.USER ||
      hasReportedRef.current ||
      msg.read // 如果消息已经是已读状态，不再处理
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasReportedRef.current) {
            // 当消息在视口中出现至少0.5秒后上报
            setTimeout(() => {
              /**
               * 上报的ID设置clientId的主要目的是，有些消息是有客户端这边主动插入的, 比如进入会话时问题类型选择，此时只有ID，此时上报可以作为消息推送的依据
               */
              const reportId = msg.id;
              const uniqueKey = `chatId_${reportId}`;
              const beRead = msg.beRead;

              // 添加到上报队列
              if (reportId && !reportQueue.has(uniqueKey) && !beRead) {
                reportQueue.set(uniqueKey, {
                  id: reportId,
                  timestamp: Date.now(),
                  observer: observer,
                  key: uniqueKey,
                });
                // 安排上报
                scheduleNextReport();
              }
              // 标记消息为已读
              markMessageAsRead(reportId as string);
              hasReportedRef.current = true;
              observer.disconnect();
            }, 500);
          }
        });
      },
      { threshold: 0.9 },
    ); // 至少90%可见时触发
    observer.observe(itemRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg.id]);

  return {
    itemRef,
  };
};
